import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { doctorTeamApi } from '@/api/doctorTeam';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

function fmtMoney(v: number) {
  return inr.format(v || 0);
}

type Tab = 'overview' | 'opd' | 'procedures' | 'packages' | 'patients' | 'team';

export default function DoctorTeamDashboard() {
  const [from, setFrom] = useState(() =>
    new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)
  );
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [tab, setTab] = useState<Tab>('overview');

  const query = useQuery({
    queryKey: ['doctor-team-dashboard', from, to],
    queryFn: async () => {
      const res = await doctorTeamApi.getDashboard({ from, to });
      return res.data.data;
    },
  });

  const data = query.data;
  const patients = data?.patients ?? [];

  const filteredPatients = useMemo(() => {
    if (tab === 'opd') return patients.filter((p) => p.category === 'OPD');
    if (tab === 'procedures') return patients.filter((p) => p.category === 'PROCEDURE');
    if (tab === 'packages') return patients.filter((p) => p.category === 'PACKAGE');
    return patients;
  }, [patients, tab]);

  return (
    <div className="space-y-4 max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Team performance</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data?.teamLead?.name ? `${data.teamLead.name} — team dashboard` : 'Loading team…'}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" />
          </div>
          <Button variant="outline" size="sm" onClick={() => query.refetch()} disabled={query.isFetching}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-2">
        {(
          [
            ['overview', 'Overview'],
            ['opd', 'OPD'],
            ['procedures', 'Procedures'],
            ['packages', 'Packages'],
            ['patients', 'All patients'],
            ['team', 'By doctor'],
          ] as const
        ).map(([k, label]) => (
          <Button
            key={k}
            size="sm"
            variant={tab === k ? 'default' : 'outline'}
            onClick={() => setTab(k)}
          >
            {label}
          </Button>
        ))}
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading dashboard…</p>
      ) : (
        <>
          {(tab === 'overview' || tab === 'opd' || tab === 'procedures' || tab === 'packages') && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {tab === 'overview' && (
                <>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">Total revenue</CardTitle>
                    </CardHeader>
                    <CardContent className="text-2xl font-semibold">
                      {fmtMoney(data?.summary?.totalRevenue ?? 0)}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">Dr Lead patients</CardTitle>
                    </CardHeader>
                    <CardContent className="text-2xl font-semibold text-emerald-800">
                      {data?.summary?.doctorLeadPatients ?? 0}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">TRUE Lead patients</CardTitle>
                    </CardHeader>
                    <CardContent className="text-2xl font-semibold">
                      {data?.summary?.trueLeadPatients ?? 0}
                    </CardContent>
                  </Card>
                </>
              )}
              {(tab === 'overview' || tab === 'opd') && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-muted-foreground">OPD</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-semibold">{data?.summary?.opdPatients ?? 0} patients</div>
                    <div className="text-sm text-muted-foreground">{fmtMoney(data?.summary?.opdRevenue ?? 0)}</div>
                  </CardContent>
                </Card>
              )}
              {(tab === 'overview' || tab === 'procedures') && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-muted-foreground">Procedures</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-semibold">{data?.summary?.procedurePatients ?? 0} patients</div>
                    <div className="text-sm text-muted-foreground">
                      {fmtMoney(data?.summary?.procedureRevenue ?? 0)}
                    </div>
                  </CardContent>
                </Card>
              )}
              {(tab === 'overview' || tab === 'packages') && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-muted-foreground">Packages</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-semibold">{data?.summary?.packagePatients ?? 0} patients</div>
                    <div className="text-sm text-muted-foreground">
                      {fmtMoney(data?.summary?.packageRevenue ?? 0)}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {tab === 'team' && (
            <Card>
              <CardHeader>
                <CardTitle>Performance by doctor</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="min-w-max text-sm w-full">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="p-2">Doctor</th>
                      <th className="p-2 text-right">OPD</th>
                      <th className="p-2 text-right">OPD ₹</th>
                      <th className="p-2 text-right">Procedures</th>
                      <th className="p-2 text-right">Proc. ₹</th>
                      <th className="p-2 text-right">Packages</th>
                      <th className="p-2 text-right">Pkg ₹</th>
                      <th className="p-2 text-right font-medium">Total ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.byDoctor ?? []).length === 0 ? (
                      <tr>
                        <td colSpan={8} className="p-4 text-center text-muted-foreground">
                          No team doctors linked yet.
                        </td>
                      </tr>
                    ) : (
                      (data?.byDoctor ?? []).map((row) => (
                        <tr key={row.doctorId} className="border-b">
                          <td className="p-2 font-medium">{row.doctorName}</td>
                          <td className="p-2 text-right">{row.opdPatients}</td>
                          <td className="p-2 text-right">{fmtMoney(row.opdRevenue)}</td>
                          <td className="p-2 text-right">{row.procedurePatients}</td>
                          <td className="p-2 text-right">{fmtMoney(row.procedureRevenue)}</td>
                          <td className="p-2 text-right">{row.packagePatients}</td>
                          <td className="p-2 text-right">{fmtMoney(row.packageRevenue)}</td>
                          <td className="p-2 text-right font-semibold">{fmtMoney(row.totalRevenue)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {(tab === 'patients' || tab === 'opd' || tab === 'procedures' || tab === 'packages') && (
            <Card>
              <CardHeader>
                <CardTitle>Patient detail</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Lead source shown as Dr Lead vs TRUE Lead only on this dashboard. Internal reports keep full
                  source names (Google, Meta, etc.).
                </p>
              </CardHeader>
              <CardContent className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="min-w-max text-xs w-full">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="text-muted-foreground">
                      <th className="p-2 text-left">Patient</th>
                      <th className="p-2 text-left">Phone</th>
                      <th className="p-2 text-left">Category</th>
                      <th className="p-2 text-left">Doctor</th>
                      <th className="p-2 text-left">Lead bucket</th>
                      <th className="p-2 text-right">Revenue</th>
                      <th className="p-2 text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPatients.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-4 text-center text-muted-foreground">
                          No patients in this period.
                        </td>
                      </tr>
                    ) : (
                      filteredPatients.map((p, idx) => (
                        <tr key={`${p.patientId}-${p.serviceAt}-${idx}`} className="border-t">
                          <td className="p-2 font-medium">{p.patientName}</td>
                          <td className="p-2">{p.phone}</td>
                          <td className="p-2">{p.category}</td>
                          <td className="p-2">{p.doctorName}</td>
                          <td className="p-2">
                            <span
                              className={`rounded px-1.5 py-0.5 font-medium ${
                                p.leadBucket === 'DOCTOR_LEAD'
                                  ? 'bg-emerald-100 text-emerald-900'
                                  : 'bg-slate-100 text-slate-800'
                              }`}
                            >
                              {p.leadBucketLabel}
                            </span>
                          </td>
                          <td className="p-2 text-right">{fmtMoney(p.amount)}</td>
                          <td className="p-2 whitespace-nowrap">
                            {new Date(p.serviceAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
